import React from 'react';
import PropTypes from 'prop-types';
import EditAccount from './EditAccount/EditAccountView';
import AddAccount from './AddAccount/AddAccountView';
import { VIEWS } from './UpsertAccountModalConstants';
import { StyledModal } from './UpsertAccountModalStyles';
import PlaidLinkAccount from './PlaidLinkAccount/PlaidLinkAccountView';
import QuovoSearchInstitutionView from '../Quovo/components/QuovoSearchInstitution/QuovoSearchInstitutionView';

const renderCurrentView = ({
  currentClient,
  currentView,
  setCurrentView,
  focusedAccount,
  visibleAccountTypesStack,
  pushVisibleAccountTypes,
  popVisibleAccountTypes,
  setFocusedAccount,
  onDeleteAccount,
  onSaveAccount,
  setErrors,
  errors,
  isSavingAccount,
  isDeletingAccount,
  defaultPrincipalOwner,
  accountTypesDictionary,
  otherAssetTypesDictionary,
  isFocusedAccountNew,
  isConfirmationModalOpen,
  setIsConfirmationModalOpen,
  fetchNetWorthData,
  isFocusedAccountEditable,
  onOpenAkoyaLinkManagementModal,
}) => {
  switch (currentView) {
    case VIEWS.PLAID:
      return (
        <PlaidLinkAccount
          currentClient={currentClient}
          onAddAccount={() => {
            setCurrentView(VIEWS.ADD);
          }}
          onAccountLinked={() => {
            setCurrentView(VIEWS.LIST);
            fetchNetWorthData(currentClient.Id);
          }}
          onOpenAkoyaLinkManagementModal={onOpenAkoyaLinkManagementModal}
        />
      );
    case VIEWS.LINK:
      return (
        <QuovoSearchInstitutionView
          isWaitingForResults={false}
          onManualAddAccountPressed={() => () => setCurrentView(VIEWS.ADD)}
          workflowId="FinancialAccountsWorkflow"
          clientId={currentClient.Id}
          hasFidelity={currentClient.hasFidelity}
          onFidelityClick={() => {
            setCurrentView(VIEWS.LIST);
            fetchNetWorthData(currentClient.Id);
          }}
        />
      );
    case VIEWS.ADD:
      return (
        <AddAccount
          visibleAccountTypesStack={visibleAccountTypesStack}
          pushVisibleAccountTypes={pushVisibleAccountTypes}
          popVisibleAccountTypes={popVisibleAccountTypes}
          setCurrentView={setCurrentView}
          setFocusedAccount={setFocusedAccount}
          defaultPrincipalOwner={defaultPrincipalOwner}
        />
      );
    case VIEWS.EDIT:
      return (
        <EditAccount
          account={focusedAccount}
          setCurrentView={setCurrentView}
          onDeleteAccount={onDeleteAccount}
          onSaveAccount={onSaveAccount}
          setFocusedAccount={setFocusedAccount}
          setErrors={setErrors}
          errors={errors}
          isSavingAccount={isSavingAccount}
          isDeletingAccount={isDeletingAccount}
          accountTypesDictionary={accountTypesDictionary}
          otherAssetTypesDictionary={otherAssetTypesDictionary}
          isFocusedAccountNew={isFocusedAccountNew}
          isFocusedAccountEditable={isFocusedAccountEditable}
          isConfirmationModalOpen={isConfirmationModalOpen}
          setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        />
      );
    default:
      return null;
  }
};

const UpsertAccountModalView = ({
  currentClient,
  currentView,
  setCurrentView,
  focusedAccount,
  visibleAccountTypesStack,
  pushVisibleAccountTypes,
  popVisibleAccountTypes,
  setFocusedAccount,
  onDeleteAccount,
  onSaveAccount,
  setErrors,
  errors,
  isSavingAccount,
  isDeletingAccount,
  defaultPrincipalOwner,
  accountTypesDictionary,
  otherAssetTypesDictionary,
  isFocusedAccountNew,
  isConfirmationModalOpen,
  setIsConfirmationModalOpen,
  fetchNetWorthData,
  redirectUrl,
  isFocusedAccountEditable,
  onOpenAkoyaLinkManagementModal,
}) => (
  <StyledModal
    isOpen={currentView !== VIEWS.LIST}
    showCloseModalCross
    onRequestClose={() => setCurrentView(VIEWS.LIST)}
    currentView={currentView}
  >
    {renderCurrentView({
      currentClient,
      currentView,
      setCurrentView,
      focusedAccount,
      visibleAccountTypesStack,
      pushVisibleAccountTypes,
      popVisibleAccountTypes,
      setFocusedAccount,
      onDeleteAccount: onDeleteAccount.bind(null, currentClient.Id),
      onSaveAccount: account =>
        onSaveAccount(currentClient.Id, { ...account, ClientId: currentClient.Id }, redirectUrl),
      setErrors,
      errors,
      isSavingAccount,
      isDeletingAccount,
      defaultPrincipalOwner,
      accountTypesDictionary,
      otherAssetTypesDictionary,
      isFocusedAccountNew,
      isFocusedAccountEditable,
      isConfirmationModalOpen,
      setIsConfirmationModalOpen,
      fetchNetWorthData,
      onOpenAkoyaLinkManagementModal,
    })}
  </StyledModal>
);

UpsertAccountModalView.defaultProps = {
  focusedAccount: null,
  defaultPrincipalOwner: null,
  redirectUrl: null,
};

UpsertAccountModalView.propTypes = {
  currentClient: PropTypes.shape().isRequired,
  currentView: PropTypes.string.isRequired,
  setCurrentView: PropTypes.func.isRequired,
  focusedAccount: PropTypes.shape(),
  visibleAccountTypesStack: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  ).isRequired,
  pushVisibleAccountTypes: PropTypes.func.isRequired,
  popVisibleAccountTypes: PropTypes.func.isRequired,
  setFocusedAccount: PropTypes.func.isRequired,
  onDeleteAccount: PropTypes.func.isRequired,
  onSaveAccount: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  isSavingAccount: PropTypes.bool.isRequired,
  isDeletingAccount: PropTypes.bool.isRequired,
  defaultPrincipalOwner: PropTypes.string,
  accountTypesDictionary: PropTypes.shape().isRequired,
  otherAssetTypesDictionary: PropTypes.shape().isRequired,
  isFocusedAccountNew: PropTypes.bool.isRequired,
  isConfirmationModalOpen: PropTypes.bool.isRequired,
  setIsConfirmationModalOpen: PropTypes.func.isRequired,
  fetchNetWorthData: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  isFocusedAccountEditable: PropTypes.bool.isRequired,
  onOpenAkoyaLinkManagementModal: PropTypes.func.isRequired,
};

export default UpsertAccountModalView;
